import { useCallback, useMemo } from "react";
import { Stack } from "@/components/layout";
import { SectionHeader } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { HubEmbeddedProvider, HubTabs, useHubLoad } from "@/modules/hub-kit/HubKit";
import { FinanceExportsTab } from "../components/FinanceExportsTab";
import { FinanceFeesTab } from "../components/FinanceFeesTab";
import { FinanceMembershipsTab } from "../components/FinanceMembershipsTab";
import { FinanceOverviewTab } from "../components/FinanceOverviewTab";
import { FinanceReconciliationTab } from "../components/FinanceReconciliationTab";
import { createFinanceOverviewApi } from "../overviewApi";
import {
  DisputesQueuePage,
  PayoutsQueuePage,
  RefundsQueuePage,
  SettlementsQueuePage,
  TransactionsQueuePage,
} from "./FinanceQueuePages";

const CRUMBS = [{ label: "HUDDL HQ", to: "/app" }, { label: "Finance" }];

/**
 * Finance: money in, money out, and whether the two agree.
 * One overview, then a tab per job. Each tab is a real queue or a real
 * read of the ledger; nothing is a placeholder.
 */
export function FinanceHubPage() {
  usePageBreadcrumbs(CRUMBS);
  const client = useApiClient();
  const api = useMemo(() => createFinanceOverviewApi(client), [client]);

  // Counts for the tab badges. Failing here only hides the badges.
  const loadCounts = useCallback(async () => {
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 86400000);
    return (await api.getOverview(from.toISOString(), to.toISOString())).overview.queues;
  }, [api]);
  const { data: q } = useHubLoad(loadCounts);

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Finance"
        description="Money in, money out, and whether the ledger agrees with itself. HQ records and reconciles; Xendit moves the money."
      />
      <HubEmbeddedProvider crumbs={CRUMBS}>
        <HubTabs
          items={[
            { id: "overview", label: "Overview", content: <FinanceOverviewTab /> },
            { id: "memberships", label: "Memberships", content: <FinanceMembershipsTab /> },
            { id: "payments", label: "Payments", content: <TransactionsQueuePage /> },
            {
              id: "payouts",
              label: "Payouts",
              ...(q?.payouts_to_action ? { badge: q.payouts_to_action } : {}),
              content: (
                <HubTabs
                  param="sub"
                  variant="sub"
                  items={[
                    { id: "payouts", label: "Payouts", content: <PayoutsQueuePage /> },
                    {
                      id: "settlements",
                      label: "Settlements",
                      ...(q?.settlements_unmatched ? { badge: q.settlements_unmatched, badgeTone: "danger" as const } : {}),
                      content: <SettlementsQueuePage />,
                    },
                  ]}
                />
              ),
            },
            {
              id: "refunds",
              label: "Refunds & disputes",
              ...(q ? { badge: q.refunds_open + q.disputes_open } : {}),
              content: (
                <HubTabs
                  param="sub"
                  variant="sub"
                  items={[
                    {
                      id: "refunds",
                      label: "Refunds",
                      ...(q?.refunds_open ? { badge: q.refunds_open } : {}),
                      content: <RefundsQueuePage />,
                    },
                    {
                      id: "disputes",
                      label: "Disputes",
                      ...(q?.disputes_open ? { badge: q.disputes_open } : {}),
                      content: <DisputesQueuePage />,
                    },
                  ]}
                />
              ),
            },
            { id: "fees", label: "Fees & commission", content: <FinanceFeesTab /> },
            {
              id: "reconciliation",
              label: "Reconciliation",
              ...(q?.exceptions_open
                ? { badge: q.exceptions_open, badgeTone: q.exceptions_open_critical ? ("danger" as const) : ("warning" as const) }
                : {}),
              content: <FinanceReconciliationTab />,
            },
            { id: "exports", label: "Exports", content: <FinanceExportsTab /> },
          ]}
        />
      </HubEmbeddedProvider>
    </Stack>
  );
}

